// The name the institution gives the PQRSD module (config/pqrsd.moduleLabel),
// e.g. "Solicitudes" or "PQRSDF". It replaces the word "PQRSD" in what people
// read: the app, the public pages and the emails. Codes never change: radicado
// prefixes (PQR_…), routes (/pqrsd/…), permissions and Firestore values.
//
// Shared by the functions (email templates) and the frontend (pqrsdText).
// Plain CommonJS so the functions can require it and webpack can import it.

const DEFAULT_MODULE_LABEL = "PQRSD";
const MAX_MODULE_LABEL = 30;
const DEFAULT_MODULE_SLUG = "pqrsd";

// The citizen pages (landing, form, status) live at /<slug>, /<slug>/radicar
// and /<slug>/estado, where the slug comes from the module name. Staff routes
// stay under /pqrsd. A name whose slug is already a top-level route of the app
// is refused, or the public pages would shadow it. Keep in sync with
// src/MyRouter.js (moduleLabel.test.js checks it).
const RESERVED_SLUGS = [
  "admin", "agreements", "applicants", "audit", "config", "contracts", "entities", "expedients",
  "extension", "filed", "filedlist", "financing", "flow", "home", "invitation", "labs", "login",
  "migration", "my-entity", "mytasks", "password-reset", "payments", "process", "processtemplates",
  "processes", "processestemplate", "profile", "reports", "richeditor", "submission", "task", "trd", "users",
  // Not routes today, but would read as app paths.
  "api", "static", "assets", "logout", "estado", "radicar",
];

// "Atención R&D" → "atencion-r-d"; the default name → "pqrsd".
function slugify(raw) {
  return String(raw == null ? "" : raw)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// The word as the texts write it: "PQRSD", its plural "PQRSDs", and the
// "PQRSd" spelling some screens use. Case-sensitive on purpose, so lowercase
// routes and ids ("/pqrsd/estado", "pqrsd.reports") are never touched, and
// bounded so PQRSD_STATUS or PQR_2026… are not either.
const WORD = /\bPQRSD[sS]?\b|\bPQRSd\b/g;

// A usable label, or the default. Letters (accents included), digits, spaces
// and . - & / only: it lands in email subjects and HTML.
function normalizeModuleLabel(raw) {
  const label = String(raw == null ? "" : raw)
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_MODULE_LABEL);
  if (!label || !/^[\p{L}\p{N} .&/-]+$/u.test(label)) return DEFAULT_MODULE_LABEL;
  // A name that still contains the word would grow each time a text is
  // relabeled twice ("PQRSD y F" → "PQRSD y F y F"). "PQRSDF" is fine.
  if (label !== DEFAULT_MODULE_LABEL && new RegExp(WORD.source).test(label)) return DEFAULT_MODULE_LABEL;
  const slug = slugify(label);
  if (label !== DEFAULT_MODULE_LABEL && (!slug || RESERVED_SLUGS.includes(slug))) return DEFAULT_MODULE_LABEL;
  return label;
}

// The URL segment of the citizen pages for a module name.
function moduleSlug(label) {
  const name = normalizeModuleLabel(label);
  return name === DEFAULT_MODULE_LABEL ? DEFAULT_MODULE_SLUG : slugify(name);
}

// Why the admin's input cannot be saved (Spanish), or null.
function moduleLabelError(raw) {
  const label = String(raw == null ? "" : raw).replace(/\s+/g, " ").trim();
  if (!label) return "Escriba el nombre del módulo.";
  if (label.length > MAX_MODULE_LABEL) return `Máximo ${MAX_MODULE_LABEL} caracteres.`;
  if (!/^[\p{L}\p{N} .&/-]+$/u.test(label)) return "Use solo letras, números, espacios y . - & /";
  if (label !== DEFAULT_MODULE_LABEL && new RegExp(WORD.source).test(label)) {
    return "No puede contener la palabra PQRSD suelta (sí, por ejemplo, PQRSDF).";
  }
  const slug = slugify(label);
  if (label !== DEFAULT_MODULE_LABEL && !slug) return "Incluya al menos una letra o número.";
  if (label !== DEFAULT_MODULE_LABEL && RESERVED_SLUGS.includes(slug)) {
    return `La dirección /${slug} ya la usa otra parte de la plataforma; elija otro nombre.`;
  }
  return null;
}

function relabel(text, label) {
  const name = normalizeModuleLabel(label);
  if (typeof text !== "string" || name === DEFAULT_MODULE_LABEL) return text;
  return text.replace(WORD, name);
}

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// For email HTML: the label is escaped, since an admin may type "R&D".
function relabelHtml(html, label) {
  const name = normalizeModuleLabel(label);
  if (typeof html !== "string" || name === DEFAULT_MODULE_LABEL) return html;
  return html.replace(WORD, escapeHtml(name));
}

module.exports = {
  DEFAULT_MODULE_LABEL,
  DEFAULT_MODULE_SLUG,
  RESERVED_SLUGS,
  moduleSlug,
  slugify,
  MAX_MODULE_LABEL,
  normalizeModuleLabel,
  moduleLabelError,
  relabel,
  relabelHtml,
};
